import React from "react";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing, borderRadius } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { DropdownArrow } from "../../components/Navbar";
import { useTranslation } from "react-i18next";

const UserDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const UserDropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border: none;
  border-radius: ${borderRadius.small}px;
  background-color: ${colors.backgroundDark};
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.backgroundDark};
    opacity: 0.9;
    color: ${colors.primary};
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean; $openUpward: boolean }>`
  position: absolute;
  top: ${({ $openUpward }) => ($openUpward ? "auto" : `calc(100% + ${spacing.x1}px)`)};
  bottom: ${({ $openUpward }) => ($openUpward ? `calc(100% + ${spacing.x1}px)` : "auto")};
  right: 0;
  min-width: 180px;
  background-color: ${colors.background};
  border: none;
  border-radius: ${borderRadius.regular}px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
  transform: ${({ $isOpen }) => ($isOpen ? "translateY(0)" : "translateY(-8px)")};
  transition: all 0.2s;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownMenuItem = styled.button`
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border: none;
  background: none;
  color: ${colors.text};
  font-size: ${fontSizes.small}px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${colors.backgroundDark};
  }

  &:first-child {
    border-bottom: 1px solid ${colors.border};
    color: ${colors.textMuted};
    font-weight: 500;
    cursor: default;
    &:hover {
      background-color: ${colors.background};
    }
  }

  &:last-child {
    color: ${colors.danger};
    &:hover {
      background-color: ${colors.dangerBackground};
      color: ${colors.dangerText};
    }
  }
`;

interface UserDropdownProps {
  userEmail: string;
  onSignOut: () => void;
  openUpward?: boolean;
}

export const UserDropdown = ({
  userEmail,
  onSignOut,
  openUpward = false,
}: UserDropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-user-dropdown]')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <UserDropdownContainer data-user-dropdown>
      <UserDropdownTrigger onClick={() => setIsOpen(!isOpen)}>
        <span>{userEmail}</span>
        <DropdownArrow $isOpen={isOpen} />
      </UserDropdownTrigger>
      <DropdownMenu $isOpen={isOpen} $openUpward={openUpward}>
        <DropdownMenuItem disabled>{userEmail}</DropdownMenuItem>
        <DropdownMenuItem onClick={onSignOut}>{t("userDropdown.signOut")}</DropdownMenuItem>
      </DropdownMenu>
    </UserDropdownContainer>
  );
};
